import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminUserService } from "./adminUserService";
import { IAdminUser, ICreateAdminUser, IUpdateAdminUser } from "./IAdminUser";
import { Box, Button, Card, CardContent, Grid, LinearProgress } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from "react-toastify";
import { Facilitator } from "../facilitator";
import CustomLoadingCellRenderer from "../../common/components/CustomLoadingCellRenderer";
import { AdminUserDialog } from "./AdminUserDialog";

export const AdminUsersList = () => {
    const [adminUsers, setAdminUsers] = useState<IAdminUser[]>([]);
    const [gridApi, setGridApi] = useState<GridApi | undefined>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<IAdminUser | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadAdminUsers();
    }, []);

    const loadAdminUsers = () => {
        setLoading(true);
        AdminUserService.getAdminUsers().then(res => {
            setAdminUsers(res.data.list);
        }).catch(() => {
            toast.error("Failed to load admin users");
        }).finally(() => setLoading(false));
    };

    const handleEditClick = (user: IAdminUser) => {
        setSelectedUser(user);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDeleteClick = (user: IAdminUser) => {
        if (!window.confirm(`Delete admin user "${user.userName}"?`)) return;
        AdminUserService.deleteAdminUser({ userId: user.userId }).then(() => {
            toast.success("User deleted");
            loadAdminUsers();
        }).catch(() => {
            toast.error("Failed to delete user");
        });
    };

    const handleSaveAdminUser = (userData: ICreateAdminUser | IUpdateAdminUser) => {
        const savePromise =
            "userId" in userData
                ? AdminUserService.updateAdminUser(userData)
                : AdminUserService.createAdminUser(userData);

        savePromise.then(() => {
            toast.success(`User ${"userId" in userData ? "updated" : "created"} successfully`);
            setDialogOpen(false);
            loadAdminUsers();
        }).catch(() => {
            toast.error(`Error ${"userId" in userData ? "updating" : "creating"} user`);
        });
    };

    const columnDefs = [
        { headerName: "User Name", field: "userName" },
        { headerName: "Email", field: "email" },
        { headerName: "Mobile", field: "mobileNumber" },
        { headerName: "Address", field: "address" },
        {
            headerName: "Actions",
            field: "actions",
            cellRendererFramework: (params: any) => (
                <>
                    <Button size="small" onClick={() => handleEditClick(params.data)}>
                        <EditIcon />
                    </Button>
                    <Button size="small" color="error" onClick={() => handleDeleteClick(params.data)}>
                        <DeleteOutlinedIcon />
                    </Button>
                </>
            )
        }
    ];
    const loadingCellRenderer = useCallback(CustomLoadingCellRenderer, []);
    const loadingCellRendererParams = useMemo(() => {
        return {
            loadingMessage: "One moment please...",
        };
    }, []);
    const handleGridReady = (gridParams: GridReadyEvent) => {
        setGridApi(gridParams.api);
        gridParams.api.sizeColumnsToFit();
    };
    const handleAddAdminClick = () => {
        setSelectedUser(null);
        setEditMode(false);
        setDialogOpen(true);
    };
    return (
        <Facilitator>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                <Grid item xs={12}>
                    <Card sx={{ height: "100%" }}>
                        <CardContent>
                            <Grid container justifyContent="flex-end" spacing={2}>

                                <Grid item >  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddAdminClick}>
                                    Add Admin
                                </Button></Grid>


                            </Grid>

                            {loading && <LinearProgress sx={{ mb: 1 }} />}
                            <div
                                className="ag-theme-alpine"
                                style={{
                                    height: "75vh",
                                    width: "100%",
                                    marginTop: "15px",
                                }}
                            >
                                <AgGridReact
                                    loadingCellRenderer={loadingCellRenderer}
                                    loadingCellRendererParams={loadingCellRendererParams}
                                    onGridReady={handleGridReady}
                                    gridOptions={{
                                        pagination: true,
                                        paginationPageSize: 10,
                                        defaultColDef: {
                                            filter: true,
                                            flex: 1,
                                            floatingFilter: false,
                                            resizable: true,
                                            sortable: true,
                                        },
                                    }}
                                    rowData={adminUsers}
                                    columnDefs={columnDefs}
                                    animateRows={true}
                                    rowSelection="multiple"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <AdminUserDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSaveAdminUser}
                initialData={selectedUser ?? undefined}
                isEdit={editMode}
            />
        </Facilitator>
    );
};